import { computed, ref, watch } from 'vue';
import { useI18n } from 'vue-i18n';

import useTranslator from '@/shared/composables/useTranslator';

/**
 * Access utility functions to make working with Uppy easier.
 *
 * @returns {{
 *  getUploadAPIEndpoint: () => string
 *  getUppyLocale: ComputedRef<object>
 * }}
 */
export default function useUppyUtils() {
  const i18n = useI18n();
  const { translateText } = useTranslator();

  // Create refs for all translation strings so we can track their updates
  const addingMoreFilesRef = translateText('Adding more files');
  const addMoreRef = translateText('Add more');
  const addMoreFilesRef = translateText('Add more files');
  const backRef = translateText('Back');

  // Uppy uses %{browseFiles} placeholder interpolation to create clickable links
  // We need to translate "browse files" separately and use the placeholder in other strings
  const browseFilesTextRef = translateText('browse files');
  const browseFoldersTextRef = translateText('browse folders');
  const dropFilesHereOrRef = translateText('Drop files here or');
  // Use placeholder interpolation so Uppy can create the clickable link
  const browseFilesRef = computed(() => `${dropFilesHereOrRef.value} %{browseFiles}`);
  const browseFoldersRef = computed(() => `${dropFilesHereOrRef.value} %{browseFolders}`);

  const cancelRef = translateText('Cancel');
  const cancelUploadRef = translateText('Cancel upload');
  const completeRef = translateText('Complete');
  const doneRef = translateText('Done');
  const dropHintRef = translateText('Drop your files here');
  const removeFileRef = translateText('Remove file');
  const saveRef = translateText('Save');
  const uploadCompleteRef = translateText('Upload complete');
  const uploadRef = translateText('Upload');
  const uploadingRef = translateText('Uploading');
  const fileRef = translateText('file');
  const filesRef = translateText('files');
  const fileSelectedRef = translateText('file selected');
  const filesSelectedRef = translateText('files selected');

  // Track all translation refs for waiting on translations
  const allTranslationRefs = [
    addingMoreFilesRef,
    addMoreRef,
    addMoreFilesRef,
    backRef,
    browseFilesTextRef,
    browseFoldersTextRef,
    dropFilesHereOrRef,
    cancelRef,
    cancelUploadRef,
    completeRef,
    doneRef,
    dropHintRef,
    removeFileRef,
    saveRef,
    uploadCompleteRef,
    uploadRef,
    uploadingRef,
    fileRef,
    filesRef,
    fileSelectedRef,
    filesSelectedRef,
  ];

  const getUppyLocale = computed(() => {
    // Access i18n.locale to make this computed reactive to locale changes
    // eslint-disable-next-line no-unused-expressions
    i18n.locale.value;

    // Access .value on each ref so the computed tracks them and re-runs when they update
    return {
      strings: {
        addingMoreFiles: addingMoreFilesRef.value,
        addMore: addMoreRef.value,
        addMoreFiles: addMoreFilesRef.value,
        back: backRef.value,
        browseFiles: browseFilesTextRef.value,
        browseFolders: browseFoldersTextRef.value,
        cancel: cancelRef.value,
        cancelUpload: cancelUploadRef.value,
        complete: completeRef.value,
        done: doneRef.value,
        dropHint: dropHintRef.value,
        dropPasteBoth: browseFilesRef.value + ' ',
        dropPasteFiles: browseFilesRef.value + ' ',
        dropPasteFolders: browseFoldersRef.value + ' ',
        removeFile: removeFileRef.value,
        save: saveRef.value,
        uploadComplete: uploadCompleteRef.value,
        uploadXFiles: {
          0: uploadRef.value + ' %{smart_count} ' + fileRef.value,
          1: uploadRef.value + ' %{smart_count} ' + filesRef.value,
        },
        uploadingXFiles: {
          0: uploadingRef.value + ' %{smart_count} ' + fileRef.value,
          1: uploadingRef.value + ' %{smart_count} ' + filesRef.value,
        },
        xFilesSelected: {
          0: '%{smart_count} ' + fileSelectedRef.value,
          1: '%{smart_count} ' + filesSelectedRef.value,
        },
      },
    };
  });

  /**
   * Wait for translations to complete after a locale change.
   * For non-English locales, this polls until translations are no longer the English input text,
   * or until a timeout is reached. For English, returns immediately.
   */
  async function waitForTranslations(timeoutMs = 2000) {
    const isEnglish = i18n.locale.value === 'en' || i18n.locale.value === i18n.fallbackLocale.value;
    if (isEnglish) {
      // English doesn't need translation, return immediately
      return;
    }

    const startTime = Date.now();
    // Map of input text to their refs - we'll check if values are still English
    const inputTexts = [
      'Adding more files',
      'Add more',
      'Add more files',
      'Back',
      'browse files',
      'browse folders',
      'Drop files here or',
      'Cancel',
      'Cancel upload',
      'Complete',
      'Done',
      'Drop your files here',
      'Remove file',
      'Save',
      'Upload complete',
      'Upload',
      'Uploading',
      'file',
      'files',
      'file selected',
      'files selected',
    ];

    // Poll until translations have changed from English or timeout
    return new Promise((resolve) => {
      const checkTranslations = () => {
        const allTranslated = allTranslationRefs.every((ref, index) => {
          const value = ref.value;
          const inputText = inputTexts[index];
          // Consider translated if value differs from English input
          // (accounting for computed values like browseFilesRef)
          return value !== inputText;
        });
        const elapsed = Date.now() - startTime;

        if (allTranslated || elapsed >= timeoutMs) {
          resolve();
        } else {
          // Check again after a short delay
          setTimeout(checkTranslations, 50);
        }
      };

      checkTranslations();
    });
  }

  /**
   * Creates reactive uppyProps with locale handling and sets up watchers.
   * This centralizes the common pattern used across all Uppy components.
   *
   * @param {Object} options - Configuration options
   * @param {number} [options.height=200] - Height of the Uppy dashboard
   * @param {boolean} [options.inline=true] - Whether Uppy should be inline
   * @param {boolean} [options.proudlyDisplayPoweredByUppy=false] - Whether to show Uppy branding
   * @param {string} [options.width='100%'] - Width of the Uppy dashboard
   * @returns {Object} Object containing uppyProps ref and setupUppyWithTranslations function
   */
  function createUppyPropsWithLocale(options = {}) {
    const {
      height = 200,
      inline = true,
      proudlyDisplayPoweredByUppy = false,
      width = '100%',
    } = options;

    const uppyProps = ref({
      proudlyDisplayPoweredByUppy,
      inline,
      height,
      locale: getUppyLocale.value,
      width,
    });

    // Watch locale changes and wait for translations before updating Uppy
    watch(
      () => i18n.locale.value,
      async () => {
        // Wait for translations to complete before updating Uppy
        await waitForTranslations();
        uppyProps.value.locale = getUppyLocale.value;
      },
      { immediate: true },
    );

    // Also watch getUppyLocale for when translations update (e.g., after API calls complete)
    watch(getUppyLocale, (newLocale) => {
      // Only update if locale hasn't changed (to avoid double updates)
      if (uppyProps.value.locale !== newLocale) {
        uppyProps.value.locale = newLocale;
      }
    });

    /**
     * Setup function to call in onMounted.
     * Waits for translations to complete before initializing Uppy.
     *
     * @param {Function} uppyInitCallback - Function that initializes Uppy instance
     */
    async function setupUppyWithTranslations(uppyInitCallback) {
      // Wait for initial translations to complete before initializing Uppy
      await waitForTranslations();
      uppyInitCallback();
      // Update locale after initialization
      uppyProps.value.locale = getUppyLocale.value;
    }

    return {
      uppyProps,
      setupUppyWithTranslations,
    };
  }

  return {
    getUploadAPIEndpoint,
    getUppyLocale,
    waitForTranslations,
    createUppyPropsWithLocale,
  };
}

function getUploadAPIEndpoint() {
  const path = 'attachments/upload';
  return [window.api_url, path].join('/');
}
